import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Button,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import PageLayout from "../../components/layout/PageLayout";
import Can from "../../components/permissions/Can";
import {
  useGetPaymentsQuery,
  useUpdatePaymentStatusMutation,
} from "../../store/api";
import { extractPagination } from "../../utils/response";
import { toast } from "react-toastify";
import {
  buildPaymentRelationParams,
  buildPaymentStatusFormData,
} from "./paymentApiHelpers";
import PaymentStatusChangeDialog from "./PaymentStatusChangeDialog";
import {
  getLeadNameDisplay,
  getLeadEmailDisplay,
  getLeadPhoneDisplay,
  getPaymentTypeLabel,
  getGatewayIdDisplay,
  getTransactionIdDisplay,
  getAmountDisplay,
  getTimeDisplay,
  getStatusDisplay,
  // getClientIpDisplay,
  getBrandDisplay,
  getAccountDescriptorDisplay,
  canChangePaymentStatus,
} from "./paymentRowMappers";

const PaymentsList = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
  });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusPayment, setStatusPayment] = useState(null);

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      per_page: pagination.per_page,
      paginate: 1,
      ...buildPaymentRelationParams(),
      ...(search ? { search } : {}),
    }),
    [pagination.page, pagination.per_page, search],
  );

  const { data, isFetching: loading, error } = useGetPaymentsQuery(queryParams);
  const [updatePaymentStatus, { isLoading: statusSubmitting }] =
    useUpdatePaymentStatusMutation();

  const rows = useMemo(() => {
    const list =
      data?.data?.list ??
      data?.list ??
      (Array.isArray(data?.data) ? data.data : []);
    return Array.isArray(list) ? list : [];
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const pag = extractPagination(data);
    setPagination((p) => ({ ...p, total: pag.total ?? 0 }));
  }, [data]);

  useEffect(() => {
    if (error) toast.error(error?.data?.message || "Failed to load payments");
  }, [error]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const goView = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/payments/view/${id}`);
  };

  const openStatusChange = () => {
    const row = selectedRow;
    handleCloseMenu();
    if (!row?.id || !canChangePaymentStatus(row)) return;
    setStatusPayment(row);
    setStatusDialogOpen(true);
  };

  const closeStatusDialog = () => {
    setStatusDialogOpen(false);
    setStatusPayment(null);
  };

  const handleStatusSubmit = async (payload) => {
    if (!statusPayment?.id) return;
    try {
      const fd = buildPaymentStatusFormData(payload);
      await updatePaymentStatus({ id: statusPayment.id, data: fd }).unwrap();
      toast.success("Payment status updated");
      closeStatusDialog();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update payment status");
    }
  };

  const headCellSx = { fontWeight: 600, whiteSpace: "nowrap" };
  const cellMono = { fontFamily: "monospace", fontSize: "0.8125rem" };

  const statusChipColor = (label) => {
    const lower = String(label || "").toLowerCase();
    if (
      ["paid", "completed", "complete", "approved", "success"].some((x) =>
        lower.includes(x),
      )
    ) {
      return "success";
    }
    if (["pending", "processing", "draft"].some((x) => lower.includes(x)))
      return "warning";
    if (
      ["failed", "cancelled", "canceled", "declined", "error"].some((x) =>
        lower.includes(x),
      )
    ) {
      return "error";
    }
    return "default";
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography
          variant="h4"
          sx={{ mb: 3, color: "primary.main", fontWeight: 600 }}
        >
          Payments
        </Typography>

        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search payments..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ overflowX: "auto" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={headCellSx}>Name</TableCell>
                    <TableCell sx={headCellSx}>Email</TableCell>
                    <TableCell sx={headCellSx}>Phone</TableCell>
                    <TableCell sx={headCellSx}>Brand</TableCell>
                    <TableCell sx={headCellSx}>Account descriptor</TableCell>
                    <TableCell sx={headCellSx}>Payment type</TableCell>
                    <TableCell sx={headCellSx}>Gateway </TableCell>
                    <TableCell sx={headCellSx}>Transaction ID</TableCell>
                    <TableCell sx={headCellSx}>Amount</TableCell>
                    <TableCell sx={headCellSx}>Time</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                    {/* <TableCell sx={headCellSx}>Client IP</TableCell> */}
                    <TableCell
                      sx={{ ...headCellSx, width: 72, textAlign: "center" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No payments found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const statusLabel = getStatusDisplay(row);
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => {
                            if (row.id) navigate(`/payments/view/${row.id}`);
                          }}
                          sx={{ cursor: row.id ? "pointer" : "default" }}
                          aria-label="View payment"
                        >
                          <TableCell>{getLeadNameDisplay(row)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {getLeadEmailDisplay(row)}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {getLeadPhoneDisplay(row)}
                          </TableCell>
                          <TableCell>{getBrandDisplay(row)}</TableCell>
                          <TableCell>
                            {getAccountDescriptorDisplay(row)}
                          </TableCell>
                          <TableCell>{getPaymentTypeLabel(row)}</TableCell>
                          <TableCell sx={cellMono}>
                            {getGatewayIdDisplay(row)}
                          </TableCell>
                          <TableCell sx={cellMono}>
                            {getTransactionIdDisplay(row)}
                          </TableCell>
                          <TableCell>${getAmountDisplay(row)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {getTimeDisplay(row)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={statusLabel}
                              color={statusChipColor(statusLabel)}
                              sx={{ textTransform: "capitalize" }}
                            />
                          </TableCell>
                          {/* <TableCell sx={cellMono}>{getClientIpDisplay(row)}</TableCell> */}
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            sx={{ textAlign: "center" }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, row)}
                              aria-label="Actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Can permission="payment-view">
          <MenuItem onClick={goView}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="payment-edit">
          {selectedRow && canChangePaymentStatus(selectedRow) && (
            <MenuItem onClick={openStatusChange}>
              <ListItemIcon>
                <SyncAltIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Change status</ListItemText>
            </MenuItem>
          )}
        </Can>
      </Menu>

      <PaymentStatusChangeDialog
        open={statusDialogOpen}
        payment={statusPayment}
        onClose={closeStatusDialog}
        onSubmit={handleStatusSubmit}
        submitting={statusSubmitting}
      />
    </PageLayout>
  );
};

export default PaymentsList;
